import { Flex, Skeleton } from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useUserState } from '~/atoms/UserState'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { useClipboard } from '~/hooks/useClipboard'
import { useKeyPresses } from '~/hooks/useKeyboard'
import {
  CallDetailsAnalyticsContent,
  TCallAnalyticsTab,
} from '~/pages/Calls/CallDetails/CallDetailsAnalyticsContent'
import { CallDetailsBasicContent } from '~/pages/Calls/CallDetails/CallDetailsBasicContent'
import { CallDetailsViewHeader } from '~/pages/Calls/CallDetails/CallDetailsViewHeader'
import { hasValue } from '~/utils/common'
import { downloadAudio } from '~/utils/downloadaudio'

import { CallDirection, CallInstance } from '@mangovoice/types'

export interface TranscriptAudioPlayerState {
  isAudioPlaying: boolean
  playerPosition: number
  requestedTimestamp: number
  onPlayStateChange: (isPlaying: boolean) => void
  onSetPlayerTime: (millis: number) => void
}

interface CallDetailsContentProps {
  callId?: number
  callInstance?: CallInstance
  callInstanceLoading: boolean
  onClosed?: () => void
  isModal: boolean
  onOpenModal?: () => void
  onVoicemailClick?: () => void
  initialTab?: TCallAnalyticsTab
}
export const CallDetailsContent = ({
  callId,
  callInstance,
  callInstanceLoading,
  onClosed,
  isModal,
  onOpenModal,
  initialTab,
}: CallDetailsContentProps) => {
  const [searchParams] = useSearchParams()

  const playbackTimestampRef = useRef<number>()
  const { copyToClipboard } = useClipboard()
  const { toastSuccess, toastError } = useToastAlert()

  const { callAnalyticsEnabled } = useUserState({ canAutoRefresh: true })

  const [routeTimestamp, setRouteTimestamp] = useState<number>()

  const isEligibleForTranscription = useMemo(() => {
    if (!callAnalyticsEnabled || !callInstance) return false

    // Determine if a transcript exists.
    const transcriptReady =
      Boolean(callInstance.transcript_id) ||
      callInstance.transcription_status?.startsWith('COMPLETE')

    // If a transcript exists, we consider the call eligible even if it's missed.
    if (transcriptReady) return true

    // Otherwise, require that the call is not missed and meets the other criteria.
    return (
      callAnalyticsEnabled &&
      callInstance &&
      !callInstance.is_missed &&
      callInstance.direction !== CallDirection.Internal &&
      callInstance?.transcription_status !== 'AUDIO_TOO_SHORT' &&
      callInstance?.transcription_status !== 'AUDIO_TOO_LONG'
    )
  }, [callAnalyticsEnabled, callInstance])

  const handleCopyLink = (withTimeStamp = false) => {
    copyToClipboard(
      `${window.location.origin}/calls/${callId}${withTimeStamp ? `?t=${playbackTimestampRef.current}` : ''}`,
      () => toastSuccess('Link Copied to Clipboard'),
      () => toastError('Failed to copy link to clipboard.'),
    )
  }

  const desiredRouteTimestamp = useMemo(() => {
    return searchParams.get('t') ? Number(searchParams.get('t')) : undefined
  }, [searchParams.get('t')])

  useEffect(() => {
    setRouteTimestamp(
      desiredRouteTimestamp ? Number(desiredRouteTimestamp) : undefined,
    )
  }, [callId, desiredRouteTimestamp])

  //sets up open/close animations cleanly
  const shouldBeClosed = !hasValue(callId)
  useEffect(() => {
    //when selected call id changes, always clear the playback timestamp
    playbackTimestampRef.current = undefined
  }, [callId])

  useKeyPresses({
    filter: (e) => e.key === 'Escape',
    onPress: (_) => {
      onClosed?.()
    },
  })

  const handleDownloadRecording = () => {
    if (callInstance?.recording_url) {
      downloadAudio(callInstance.recording_url, `${callInstance?.id}.mp3`)
      console.log(`downloading recording ${callInstance.recording_url}`)
    }
  }

  // useEffect(() => {
  //   if (initialTimestamp) {
  //     playbackTimestampRef.current = initialTimestamp
  //   }
  // }, [initialTimestamp])
  return (
    <Flex flex="1" flexDirection="column" bg="gray.100" width="full">
      <CallDetailsViewHeader
        callInstanceLoading={callInstanceLoading}
        callInstance={callInstance}
        shouldBeClosed={shouldBeClosed}
        onClosed={onClosed}
        isModal={isModal}
        handleCopyLink={handleCopyLink}
        onOpenModal={onOpenModal}
        onDownloadAudio={handleDownloadRecording}
      />
      {callInstanceLoading ? (
        <Flex p="1.5rem" w="full" flexDir="column" gap="1.25rem">
          <Skeleton h="5.625rem" />
          <Flex w="full" h="9.625rem" gap="1.25rem">
            <Skeleton flexGrow="1" />
            <Skeleton flexGrow="1" />
          </Flex>
          <Skeleton h="10.375rem" />
          <Skeleton h="7.75rem" />
        </Flex>
      ) : isEligibleForTranscription && callInstance ? (
        <CallDetailsAnalyticsContent
          callInstance={callInstance}
          initialTab={initialTab}
          initialTimestamp={desiredRouteTimestamp}
          onRequestedTimestampChange={setRouteTimestamp}
          playbackTimestampRef={playbackTimestampRef}
        />
      ) : (
        callInstance && (
          <CallDetailsBasicContent
            callInstance={callInstance}
            requestedTimestamp={routeTimestamp}
            playbackTimestampRef={playbackTimestampRef}
          />
        )
      )}
    </Flex>
  )
}
